import React, { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { ThreeJSOverlayView } from "@googlemaps/three";
import * as THREE from "three";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_VERSION } from "../config/googleMaps";
import "../styles/Pages.css";

// Keeps the same data
const pradeshLocations = [
  {
    id: 1,
    latitude: "22.4110376",
    longitude: "73.19762",
    city: "Asoj",
    title: "ઘર મંદિર",
    icon: "home.pn	g",
    color: 0xff6b6b,
  },
  {
    //22.411690580902928, 73.19764595478769)
    id: 2,
    latitude: "22.411690580902928",
    longitude: "73.19764595478769",
    city: "Asoj",
    title: "ચબૂતરો",
    icon: "chabutaro.png",
    color: 0x4ecdc4,
  },
  {
    id: 3,
    latitude: "22.412973",
    longitude: "73.196613",
    city: "Asoj",
    title: "તળાવ",
    icon: "lake.png",
    color: 0xffd93d,
  },
  {
    id: 4,
    latitude: "22.412591",
    longitude: "73.196090",
    city: "Asoj",
    title: "વડ",
    icon: "tree.png",
    color: 0x2ecc71,
  },
  {
    id: 5,
    latitude: "22.4110795",
    longitude: "73.1974724",
    city: "Asoj",
    title: "શ્રી રામજી મંદિર",
    icon: "temple.png",
    color: 0xe67e22,
  },
  {
    id: 6,
    latitude: "22.411217",
    longitude: "73.197650",
    city: "Asoj",
    title: "નર્મદાભાભીનું ઘર",
    icon: "home.png",
    color: 0xe91e8c,
  },
];

// Meters per degree approximation for small area
const METERS_PER_DEG_LAT = 110700;
const METERS_PER_DEG_LNG = 103300;

// Create HTML label element for AdvancedMarkerElement
function createLabelDiv(text) {
  const div = document.createElement("div");
  div.className = "asoj-map-label";
  div.textContent = text;
  div.style.background =
    "linear-gradient(135deg, #e8f0fe 0%, #d4e4ff 25%, #e6e9f9 50%, #f0e8ff 75%, #fce8f3 100%)";
  div.style.color = "#1a1a2e";
  div.style.fontWeight = "bold";
  div.style.padding = "6px 18px";
  div.style.borderRadius = "8px";
  div.style.boxShadow = "0 2px 8px #764ba233";
  div.style.cursor = "pointer";
  div.style.userSelect = "none";
  div.style.border = "2px solid #764ba2";
  div.style.display = "inline-block";
  div.style.maxWidth = "90vw";
  div.style.wordBreak = "break-word";
  div.style.whiteSpace = "normal";
  div.style.textAlign = "center";
  div.style.fontSize = "clamp(0.85em, 3vw, 1.2em)";
  div.style.padding = "clamp(4px, 2vw, 8px) clamp(10px, 5vw, 18px)";
  return div;
}

function AsojMap() {
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const labelObjectsRef = useRef([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapType, setMapType] = useState("hybrid");
  const [prasangModalOpen, setPrasangModalOpen] = useState(false);
  const [prasangHtml, setPrasangHtml] = useState("");
  // New: Preloaded HTMLs for prasang files
  const [preloadedPrasangHtmls, setPreloadedPrasangHtmls] = useState({});
  // Store prasang list for selected location
  const [asojList, setAsojList] = useState([]);
  // Font size state for prasang modal
  const [prasangFontSize, setPrasangFontSize] = useState(1);

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState(null);

  const centerLat = 22.4115; // Approximated center
  const centerLng = 73.197;

  useEffect(() => {
    let raycaster, mouse, overlay;
    const initMap = async () => {
      setOptions({
        key: GOOGLE_MAPS_API_KEY,
        version: GOOGLE_MAPS_VERSION,
      });

      const { Map } = await importLibrary("maps");
      const { AdvancedMarkerElement } = await importLibrary("marker");

      // Initialize Map
      const map = new Map(mapRef.current, {
        mapId: "15431d2b469f209e",
        center: { lat: centerLat, lng: centerLng },
        zoom: 19,
        tilt: 45,
        heading: 0,
        mapTypeId: mapType,
        disableDefaultUI: true,
        gestureHandling: "greedy",
      });

      // Initialize Three.js Scene
      const scene = new THREE.Scene();
      const ambientLight = new THREE.AmbientLight(0xffffff, 2.0);
      scene.add(ambientLight);

      // Initialize Overlay
      overlay = new ThreeJSOverlayView({
        map,
        scene,
        anchor: { lat: centerLat, lng: centerLng, altitude: 0 },
        THREE,
        animationMode: "always",
      });
      overlayRef.current = overlay;

      // Add Markers and Labels
      labelObjectsRef.current = [];
      pradeshLocations.forEach((location) => {
        const color = location.color || 0x00ff00;
        // Marker
        const markerHeight = 50;
        const curve = new THREE.LineCurve3(
          new THREE.Vector3(0, 0, 0),
          new THREE.Vector3(0, markerHeight, 0),
        );
        const geometry = new THREE.TubeGeometry(curve, 1, 0.5, 8, false);
        const material = new THREE.MeshBasicMaterial({ color: color });
        const marker = new THREE.Mesh(geometry, material);
        // Ball on top
        const topGeo = new THREE.SphereGeometry(1.5, 16, 16);
        const topMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
        const topMesh = new THREE.Mesh(topGeo, topMat);
        topMesh.position.set(0, markerHeight + 1, 0);
        marker.add(topMesh);
        // Position
        const position = overlay.latLngAltitudeToVector3({
          lat: location.latitude,
          lng: location.longitude,
          altitude: 0,
        });
        marker.position.copy(position);
        marker.userData = { location };
        marker.rotation.x = Math.PI / 2;
        scene.add(marker);
        // Label via AdvancedMarkerElement – positioned by Google Maps at lat/lng,
        // so it tracks the marker perfectly at every zoom level with no drift.
        const labelDiv = createLabelDiv(location.title);
        const labelMarker = new AdvancedMarkerElement({
          map,
          position: {
            lat: parseFloat(location.latitude),
            lng: parseFloat(location.longitude),
            altitude: markerHeight / 2,
          },
          content: labelDiv,
          gmpClickable: true,
        });
        labelDiv.addEventListener("click", async (e) => {
          e.stopPropagation();
          setSelectedLocation(location);
          setIsModalOpen(true);
          setModalContent("Loading...");
          const asojListFetched = await fetchAsojData(location.id);
          setAsojList(asojListFetched);
          const htmls = {};
          await Promise.all(
            asojListFetched.map(async (item) => {
              try {
                const res = await fetch(`/map/prasang/${item.File}`);
                if (!res.ok) {
                  htmls[item.File] = `File not found or unavailable.`;
                } else {
                  const html = await res.text();
                  htmls[item.File] = `${html}`;
                }
              } catch (err) {
                htmls[item.File] = `Error loading file.`;
              }
            }),
          );
          setPreloadedPrasangHtmls(htmls);
          setModalContent("");
        });
        labelObjectsRef.current.push(labelMarker);
      });

      raycaster = new THREE.Raycaster();
      mouse = new THREE.Vector2();
    };

    initMap();

    // Cleanup: remove AdvancedMarkerElements from the map
    return () => {
      labelObjectsRef.current.forEach((m) => {
        m.map = null;
      });
      labelObjectsRef.current = [];
    };
  }, [mapType]);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLocation(null);
    setPrasangModalOpen(false); // Also close prasang modal if open
  };

  // Helper to fetch and filter asoj.json
  async function fetchAsojData(locationId) {
    const res = await fetch("/map/asoj.json");
    const data = await res.json();
    return data.filter((item) => item.LocationId === String(locationId));
  }

  // Helper to fetch HTML file
  async function fetchHtmlFile(filePath) {
    const res = await fetch(`/map/prasang/${filePath}`);
    const text = await res.text();
    alert(text); // Show the result in a message
    return text;
  }

  return (
    <div className="page-container asoj-map-page themed-map-page scripture-reader" style={{ padding: 0, overflow: "hidden" }}>
      {/* Menu/Header is rendered by Header.js above */}

      {/* Map Only */}
      <div
        ref={mapRef}
        style={{ width: "100%", height: "80vh", display: "block" }}
      />

      {/* Map Type Buttons and Location List below the map */}
      <div
        style={{
          maxWidth: 1200,
          margin: "16px auto 0 auto",
          padding: "12px 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div className="asoj-map-types" role="group" aria-label="Map type" style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <button
            onClick={() => setMapType("hybrid")}
            aria-pressed={mapType === "hybrid"}
            style={{
              fontWeight: 600,
              fontSize: "1.1em",
              color: "#1a1a2e",
              background: mapType === "hybrid" ? "#e8f0fe" : "#fff",
              border: "1px solid #667eea",
              borderRadius: 8,
              padding: "6px 18px",
              cursor: "pointer",
              boxShadow: "0 2px 8px #667eea33",
            }}
          >
            Hybrid
          </button>
          <button
            onClick={() => setMapType("roadmap")}
            aria-pressed={mapType === "roadmap"}
            style={{
              fontWeight: 600,
              fontSize: "1.1em",
              color: "#1a1a2e",
              background: mapType === "roadmap" ? "#e8f0fe" : "#fff",
              border: "1px solid #667eea",
              borderRadius: 8,
              padding: "6px 18px",
              cursor: "pointer",
              boxShadow: "0 2px 8px #667eea33",
            }}
          >
            Roadmap
          </button>
        </div>
        <div
          className="asoj-location-list"
          style={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(232,240,254,0.85)",
            borderRadius: 12,
            boxShadow: "0 2px 8px #667eea33",
            padding: "12px 0",
          }}
        >
          {pradeshLocations.map((loc) => (
            <div
              key={loc.id}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <span
                className="asoj-location-number"
                style={{
                  fontWeight: 700,
                  fontSize: "1.1em",
                  color: "#667eea",
                  background: "#e6e9f9",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                  boxShadow: "0 2px 8px #764ba233",
                }}
              >
                {loc.id}
              </span>
              <button
                className="asoj-location-button"
                style={{
                  fontWeight: 600,
                  fontSize: "1.1em",
                  color: "#1a1a2e",
                  background: "#f0e8ff",
                  border: "1px solid #764ba2",
                  borderRadius: 8,
                  padding: "6px 18px",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px #764ba233",
                  transition: "background 0.2s",
                }}
                onClick={async () => {
                  if (overlayRef.current && overlayRef.current.map) {
                    overlayRef.current.map.setCenter({
                      lat: parseFloat(loc.latitude),
                      lng: parseFloat(loc.longitude),
                    });
                    overlayRef.current.map.setZoom(19);
                  }
                  setSelectedLocation(loc);
                  setIsModalOpen(true);
                  setModalContent("Loading...");
                  const asojListFetched = await fetchAsojData(loc.id);
                  setAsojList(asojListFetched);
                  // Preload all prasang HTML files for this location
                  const htmls = {};
                  await Promise.all(
                    asojListFetched.map(async (item) => {
                      try {
                        const res = await fetch(`/map/prasang/${item.File}`);
                        if (!res.ok) {
                          htmls[item.File] =
                            `File path: /map/prasang/${item.File}<br>File not found or unavailable.`;
                        } else {
                          const html = await res.text();
                          htmls[item.File] =
                            `File path: /map/prasang/${item.File}<br>${html}`;
                        }
                      } catch (err) {
                        htmls[item.File] =
                          `File path: /map/prasang/${item.File}<br>Error loading file.`;
                      }
                    }),
                  );
                  setPreloadedPrasangHtmls(htmls);
                  setModalContent("");
                }}
              >
                {loc.title}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Prasang Titles Modal (left-aligned, max width 300px, location title as modal title, prasang titles with srno) */}
      {isModalOpen && (
        <div
          className="modal-overlay asoj-dialog-overlay"
          style={{
            ...modalOverlayThemeStyles,
            justifyContent: "flex-start",
            alignItems: "flex-start",
            background: "rgba(232,240,254,0.85)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "row",
          }}
          onClick={closeModal}
        >
          <div
            className="modal-content"
            style={{
              ...modalThemeStyles,
              maxWidth: 480,
              minWidth: 320,
              margin: "40px 0 0 40px",
              minHeight: 120,
              height: "auto",
              boxShadow: "0 4px 32px #0003",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              textAlign: "left",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-button" aria-label="Close location" onClick={closeModal}>
              &times;
            </button>
            <h2
              className="asoj-modal-title"
              style={{
                marginTop: 0,
                marginBottom: 12,
                fontWeight: 700,
                fontSize: "1.2em",
                color: "#764ba2",
                textAlign: "left",
                width: "100%",
              }}
            >
              {selectedLocation?.title}
            </h2>
            <div style={{ width: "100%" }}>
              {asojList.length === 0 ? (
                <p>Loading...</p>
              ) : (
                <ul
                  style={{
                    padding: 0,
                    margin: 0,
                    listStyle: "none",
                    width: "100%",
                  }}
                >
                  {asojList.map((item, idx) => (
                    <li
                      key={item.Id}
                      style={{
                        marginBottom: 10,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span
                        className="asoj-prasang-number"
                        style={{
                          fontWeight: 700,
                          marginRight: 8,
                          color: "#667eea",
                        }}
                      >
                        {idx + 1}.
                      </span>
                      <button
                        className="asoj-prasang-button"
                        style={{
                          minWidth: 120,
                          fontWeight: 600,
                          cursor: "pointer",
                          background: "#e8f0fe",
                          border: "1px solid #bcd0ee",
                          borderRadius: 6,
                          padding: "6px 12px",
                          fontSize: "1em",
                        }}
                        onClick={() => {
                          const fileName = item.File;
                          if (fileName && preloadedPrasangHtmls[fileName]) {
                            setPrasangHtml(preloadedPrasangHtmls[fileName]);
                            setPrasangModalOpen(true);
                          }
                        }}
                      >
                        {item.Title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Right modal: Prasang HTML */}
      {prasangModalOpen && (
        <div
          className="modal-overlay asoj-dialog-overlay"
          style={{
            ...modalOverlayThemeStyles,
            justifyContent: "center",
            alignItems: "flex-start",
            background: "rgba(232,240,254,0.85)",
            zIndex: 2100,
            display: "flex",
            flexDirection: "row",
          }}
          onClick={() => setPrasangModalOpen(false)}
        >
          <div
            className="modal-content"
            style={{
              ...modalThemeStyles,
              maxWidth: 1000,
              minWidth: 320,
              width: "100%",
              margin: "40px auto 0 auto",
              minHeight: 320,
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 4px 32px #764ba233",
              overflow: "hidden",
              background:
                "linear-gradient(135deg, #e8f0fe 0%, #d4e4ff 25%, #e6e9f9 50%, #f0e8ff 75%, #fce8f3 100%)",
              border: "1px solid #764ba2",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-button"
              aria-label="Close prasang"
              onClick={() => setPrasangModalOpen(false)}
            >
              &times;
            </button>
            {/* Show image and video side by side above HTML content */}
            {(() => {
              const prasangItem = asojList.find(
                (item) =>
                  item.File && preloadedPrasangHtmls[item.File] === prasangHtml,
              );
              const hasImage = prasangItem && prasangItem.Image;
              const hasVideo = prasangItem && prasangItem.Video;
              if (!hasImage && !hasVideo) return null;
              return (
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: 8,
                    marginBottom: 16,
                    alignItems: "stretch",
                    height: 220,
                  }}
                >
                  {hasImage && (
                    <img
                      src={"/map/prasang/" + prasangItem.Image}
                      alt={prasangItem.Title || "Prasang Image"}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px #764ba233",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setSelectedImageSrc(
                          "/map/prasang/" + prasangItem.Image,
                        );
                        setImageModalOpen(true);
                      }}
                    />
                  )}
                  {hasVideo && (
                    <video
                      src={"/map/prasang/" + prasangItem.Video}
                      controls
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px #764ba233",
                      }}
                    />
                  )}
                </div>
              );
            })()}
            {/* Font size controls for modal */}
            <div
              className="reader-font-controls"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPrasangFontSize((s) => Math.max(s - 0.2, 0.7));
                }}
                style={{
                  padding: "2px 8px",
                  fontSize: "0.82em",
                  fontWeight: 700,
                  cursor: "pointer",
                  background: "#e8f0fe",
                  border: "1px solid #764ba2",
                  borderRadius: 5,
                  color: "#764ba2",
                  lineHeight: 1.4,
                }}
              >
                A-
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPrasangFontSize((s) => Math.min(s + 0.2, 2.5));
                }}
                style={{
                  padding: "2px 8px",
                  fontSize: "0.82em",
                  fontWeight: 700,
                  cursor: "pointer",
                  background: "#e8f0fe",
                  border: "1px solid #764ba2",
                  borderRadius: 5,
                  color: "#764ba2",
                  lineHeight: 1.4,
                }}
              >
                A+
              </button>
              <span style={{ color: "#999", fontSize: "0.78em" }}>
                {Math.round(prasangFontSize * 100)}%
              </span>
            </div>
            <div
              dangerouslySetInnerHTML={{ __html: prasangHtml }}
              style={{
                marginTop: 8,
                padding: 20,
                background: "transparent",
                borderRadius: 10,
                minHeight: 200,
                color: "#1a1a2e",
                fontFamily: "inherit",
                zoom: prasangFontSize,
                lineHeight: 1.7,
                wordBreak: "break-word",
                flex: 1,
                overflowY: "auto",
                maxHeight: "calc(85vh - 80px)",
                scrollbarColor: "#764ba2 #e8f0fe",
                scrollbarWidth: "thin",
              }}
              className="theme-scrollbar reader-html"
            />
          </div>
        </div>
      )}

      {imageModalOpen && (
        <div
          className="modal-overlay"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "rgba(0,0,0,0.7)",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 3000,
          }}
          onClick={() => setImageModalOpen(false)}
        >
          <div
            className="asoj-image-panel"
            style={{
              position: "relative",
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 4px 32px #764ba233",
              padding: "16px",
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="asoj-image-close"
              aria-label="Close image"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "#764ba2",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 32,
                height: 32,
                fontSize: 24,
                cursor: "pointer",
              }}
              onClick={() => setImageModalOpen(false)}
            >
              &times;
            </button>
            <img
              src={selectedImageSrc}
              alt="Prasang"
              style={{
                maxWidth: "80vw",
                maxHeight: "80vh",
                borderRadius: "8px",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AsojMap;

// Add custom scrollbar styles for theme
const style = document.createElement("style");
style.innerHTML = `
.theme-scrollbar::-webkit-scrollbar {
  width: 10px;
  background: transparent;
  border-radius: 8px;
}
.theme-scrollbar::-webkit-scrollbar-thumb {
  background: #764ba2;
  border-radius: 8px;
}
.theme-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #5a32a3;
}
`;
document.head.appendChild(style);

// Add modal theme styles
const modalThemeStyles = {
  background:
    "linear-gradient(135deg, #e8f0fe 0%, #d4e4ff 25%, #e6e9f9 50%, #f0e8ff 75%, #fce8f3 100%)",
  color: "#1a1a2e",
  borderRadius: "16px",
  boxShadow: "0 4px 32px #0003",
  padding: "32px 24px 24px 24px",
  maxWidth: "420px",
  margin: "auto",
  fontFamily: "inherit",
  fontSize: "1.1rem",
  border: "1px solid #e0e0e0",
};
const modalOverlayThemeStyles = {
  background: "rgba(232,240,254,0.85)",
  backdropFilter: "blur(2px)",
  zIndex: 2000,
};
